// src/services/hygieneDonationService.ts
import axios from "axios";
import { API_BASE_URL } from "../config";
import { getAuthToken } from "./authService";

/**
 * Hygiene donation APIs (.NET backend).
 * - Categories: GET /api/donations/hygiene/categories/json
 * - Opportunities: GET /api/donations/hygiene/opportunities/json
 * - DDR detail: GET /api/donations/hygiene/ddr/{id}/json
 * - Create supply request: POST /api/donations/mosques/hygiene/supply/request
 * - List supply requests: POST /api/donation/hygiene/supply/request/list
 *
 * Expected response shape from fn_list_hygiene_supply_requests_by_donor_json:
 * - Direct array: [{ supplyId, mosqueName, partnerName, state, ddr_id, ... }, ...]
 * - Or wrapped: { data: [...], pageNo, pageSize, count }
 * - Each item must include ddr_id (or DDR_Id) for the View button to appear.
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: "application/json" },
});

const getBaseURL = () =>
  (typeof process !== "undefined" && process.env?.NODE_ENV === "development") ? "" : API_BASE_URL;

function authApi() {
  const client = axios.create({
    baseURL: getBaseURL(),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    withCredentials: false,
    validateStatus: (s) => s >= 200 && s < 500,
  });
  const tok = getAuthToken();
  if (tok) client.defaults.headers.common.Authorization = `Bearer ${tok}`;
  return client;
}

export type HygieneCategoriesResponse = {
  catA: Array<{ id: number; name: string }>;
  catB: Array<{ id: number; name: string }>;
  catC: Array<{ id: number; name: string }>;
};

export async function getHygieneCategories(): Promise<HygieneCategoriesResponse> {
  const res = await api.get<HygieneCategoriesResponse>(
    "/api/donations/hygiene/categories/json"
  );
  const data = res.data;
  return {
    catA: data?.catA ?? [],
    catB: data?.catB ?? [],
    catC: data?.catC ?? [],
  };
}

export async function listHygieneOpportunities(queryString: string) {
  const res = await api.get(
    `/api/donations/hygiene/opportunities/json?${queryString}`
  );
  return res.data;
}
export async function listHygieneOpportunitiesGeo(queryString: string) {
  const res = await api.get(
    `/api/donations/hygiene/opportunities/geo/json?${queryString}`
  );
  return res.data;
}


export async function getHygieneDdrById(id: string | number) {
  const res = await api.get(
    `/api/donations/hygiene/ddr/${encodeURIComponent(String(id))}/json`
  );
  return res.data?.data ?? res.data;
}



export type CreateHygieneSupplyRequestPayload = {
  DonorIdToken: string;
  DDR_Id: number;
  mosque_id: number;
  /** معرف متعهد النظافة (اختياري) */
  supplier_id?: number;
  /** اسم متعهد النظافة (اختياري) */
  supplier_name?: string;
  /** تاريخ تنفيذ الأعمال (اختياري؛ YYYY-MM-DD) */
  maintenance_date?: string;
  /** أنا من سينفذ الأعمال — المستخدم هو المنفذ */
  is_self_provider?: boolean;
};

export async function createHygieneSupplyRequest(
  payload: CreateHygieneSupplyRequestPayload
): Promise<unknown> {
  const body: Record<string, unknown> = {
    DonorIdToken: payload.DonorIdToken,
    DDR_Id: payload.DDR_Id,
    mosque_id: payload.mosque_id,
  };
  if (payload.DonorIdToken != null && payload.DonorIdToken !== "") body.donorToken = payload.DonorIdToken;
  if (payload.supplier_id != null) body.supplier_id = payload.supplier_id;
  if (payload.supplier_name != null && payload.supplier_name !== "") body.supplier_name = payload.supplier_name;
  if (payload.maintenance_date != null && payload.maintenance_date !== "") body.maintenance_date = payload.maintenance_date;
  if (payload.is_self_provider === true) {
    body.is_self_provider = true;
    body.IsSelfProvider = true;
    body.isSelfProvider = true;
  }
  const res = await authApi().post("/api/donations/mosques/hygiene/supply/request", body);
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.data;
}

/** Body for PATCH /api/donations/hygiene/supply/request — تحديث طلب النظافة ببيانات المتعهد والتاريخ */
export type PatchHygieneSupplyRequestBody = {
  supply_id: number;
  supplier_id?: number;
  donorToken?: string;
  contract_date?: string;
  contract_duration?: number;
  requested_amount?: number;
  final_amount?: number;
  /** أنا من سينفذ الأعمال — المستخدم هو المنفذ */
  is_self_provider?: boolean;
};

/**
 * PATCH /api/donations/hygiene/supply/request
 * يحدّث طلب النظافة ببيانات المتعهد والتاريخ حتى تظهر في النظام.
 */
export async function patchHygieneSupplyRequest(
  body: PatchHygieneSupplyRequestBody
): Promise<unknown> {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    withCredentials: false,
    validateStatus: (s) => s >= 200 && s < 500,
  });
  const tok = getAuthToken();
  if (tok) client.defaults.headers.common.Authorization = `Bearer ${tok}`;
  const payload: Record<string, unknown> = {
    supply_id: body.supply_id,
    supplyId: body.supply_id,
  };
  if (body.supplier_id != null) {
    payload.supplier_id = body.supplier_id;
    payload.SupplierId = body.supplier_id;
  }
  if (body.donorToken != null && body.donorToken !== "") {
    payload.donorToken = body.donorToken;
    payload.DonorIdToken = body.donorToken;
    payload.ContributorToken = body.donorToken;
    payload.contributorToken = body.donorToken;
  }
  if (body.contract_date != null && body.contract_date !== "") payload.contract_date = body.contract_date;
  if (body.contract_duration != null) payload.contract_duration = body.contract_duration;
  if (body.requested_amount != null) payload.requested_amount = body.requested_amount;
  if (body.final_amount != null) payload.final_amount = body.final_amount;
  if (body.is_self_provider === true) {
    payload.is_self_provider = true;
    payload.IsSelfProvider = true;
    payload.isSelfProvider = true;
  }
  const res = await client.patch("/api/emasjid/patch/hygiene/supply/request", payload);
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.data;
}

/**
 * Same shape as SupplyRequestListItem for reuse in dashboard and my-requests.
 * ddr_id / DDR_Id from API is required for the "عرض" (View) button.
 */
export type HygieneSupplyRequestListItem = {
  requestId: number;
  requestName: string | null;
  mosqueId: number;
  /** معرّف الفرصة (DDR) لربط زر العرض ببيانات المسجد */
  ddrId?: number | null;
  /** رمز الطلب للملفات (إن أعاده الباك إند) */
  tokenReqId?: string | null;
  mosqueName: string | null;
  number: string | null;
  regionName: string | null;
  cityName: string | null;
  status: string | null;
  supplyId: number;
  supplyName: string | null;
  supplyDate: string | null;
  stageId: number | null;
  state: string | null;
  partnerName: string | null;
  requestedAmount: number | null;
  finalAmount: number | null;
};

function mapHygieneSupplyRow(r: any): HygieneSupplyRequestListItem {
  const supplyId = Number(
    r?.supplyId ?? r?.SupplyId ?? r?.supply_id ?? r?.Supply_Id ??
    r?.Request_Id ?? r?.request_id ?? r?.Id ?? r?.id ?? 0
  );
  const supplyName =
    r?.supplyName ?? r?.SupplyName ?? r?.supply_name ?? r?.Supply_Name ??
    r?.Request_Name ?? r?.request_name ?? r?.RequestNumber ?? r?.request_number ?? null;
  const mosqueNumber =
    r?.mosqueNumber ?? r?.MosqueNumber ?? r?.mosque_number ?? r?.Mosque_Number ??
    r?.Number ?? r?.number ?? r?.serial_number ?? r?.Serial_Number ?? null;
  const mosqueName =
    r?.mosqueName ?? r?.Mosque_Name ?? r?.mosque_name ?? null;
  const supplyDate =
    r?.supplyDate ?? r?.SupplyDate ?? r?.supply_date ?? r?.Supply_Date ??
    r?.created_at ?? r?.Created_At ?? r?.request_date ?? r?.Request_Date ??
    r?.date ?? r?.Date ?? null;
  const partnerName =
    r?.partnerName ?? r?.PartnerName ?? r?.partner_name ?? r?.Partner_Name ??
    r?.requester_name ?? r?.Requester_Name ?? r?.applicant_name ?? r?.Applicant_Name ??
    r?.donor_name ?? r?.Donor_Name ?? null;
  const state =
    r?.state ?? r?.State ?? r?.Status ?? r?.status ?? r?.StatusName ?? r?.status_name ?? null;
  return {
    requestId: supplyId,
    requestName: supplyName,
    mosqueId: Number(r?.mosqueId ?? r?.Mosque_Id ?? r?.mosque_id ?? 0),
    mosqueName: mosqueName ?? null,
    number: mosqueNumber ?? null,
    regionName: r?.regionName ?? r?.Region_Name ?? r?.region_name ?? null,
    cityName: r?.cityName ?? r?.City_Name ?? r?.city_name ?? null,
    status: state ?? null,
    supplyId,
    supplyName: supplyName ?? null,
    supplyDate: supplyDate ?? null,
    stageId: r?.stageId != null ? Number(r.stageId) : r?.StageId != null ? Number(r.StageId) : r?.stage_id != null ? Number(r.stage_id) : null,
    state: state ?? null,
    partnerName: partnerName ?? null,
    requestedAmount: typeof r?.requestedAmount === "number" ? r.requestedAmount : r?.RequestedAmount != null ? Number(r.RequestedAmount) : null,
    finalAmount: typeof r?.finalAmount === "number" ? r.finalAmount : r?.FinalAmount != null ? Number(r.FinalAmount) : null,
    ddrId: (() => {
      const v = Number(
        r?.ddr_id ?? r?.ddrId ?? r?.DDR_Id ?? r?.DDRID ?? r?.DdrId ??
        r?.opportunity_id ?? r?.opportunityId ?? r?.Opportunity_Id ?? r?.OpportunityId ??
        r?.DonationRequestId ?? r?.DonationRequest_Id ?? r?.donation_request_id ??
        r?.donation_opportunity_id ?? r?.donationOpportunityId ?? r?.DonationOpportunity_Id ??
        r?.mosque_opportunity_id ?? r?.mosqueOpportunityId ?? 0
      );
      return v > 0 ? v : null;
    })(),
    tokenReqId: r?.tokenReqId ?? r?.token_req_id ?? r?.requestToken ?? r?.RequestToken ?? null,
  };
}

export type ListHygieneSupplyRequestsParams = {
  DonorIdToken: string;
  PageNo?: number;
  PageSize?: number;
};

export type ListHygieneSupplyRequestsResult = {
  pageNo: number;
  pageSize: number;
  count: number;
  data: HygieneSupplyRequestListItem[];
};

export async function listHygieneSupplyRequestsByDonor(
  params: ListHygieneSupplyRequestsParams
): Promise<ListHygieneSupplyRequestsResult> {
  const body = {
    DonorIdToken: params.DonorIdToken,
    PageNo: params.PageNo ?? 1,
    PageSize: params.PageSize ?? 50,
  };
  const res = await authApi().post("/api/donation/hygiene/supply/request/list", body);
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  let parsed: any;
  if (typeof res.data === "string") {
    try {
      parsed = JSON.parse(res.data);
    } catch {
      parsed = {};
    }
  } else {
    parsed = res.data && typeof res.data === "object" ? res.data : {};
  }
  const raw =
    parsed?.data ?? parsed?.Data ?? parsed?.items ?? parsed?.Items ??
    parsed?.rows ?? parsed?.Rows ?? parsed?.records ?? parsed?.Records ??
    (Array.isArray(parsed) ? parsed : null);
  const arr = Array.isArray(raw) ? raw : [];
  const data = arr.map((r: any) => mapHygieneSupplyRow(r));
  if (process.env.NODE_ENV === "development" && arr.length > 0) {
    const sample = data.find((d) => !d.ddrId);
    if (sample) {
      const sampleRaw = arr.find((r: any) => {
        const v = Number(r?.ddr_id ?? r?.ddrId ?? r?.DDR_Id ?? r?.opportunity_id ?? 0);
        return v <= 0;
      });
      if (sampleRaw) {
        console.debug("[hygieneDonationService] صف بدون ddrId — عينة:", Object.keys(sampleRaw), sampleRaw);
      }
    }
  }
  return {
    pageNo: Number(parsed?.pageNo ?? parsed?.PageNo ?? 1),
    pageSize: Number(parsed?.pageSize ?? parsed?.PageSize ?? body.PageSize),
    count: Number(parsed?.count ?? parsed?.Count ?? data.length),
    data,
  };
}





// ✅ Normalize HDR detail (snake_case) → same shape used by mosque pages (camelCase)
function mapHygieneDetailToMosqueOpportunityDetail(raw: any) {
  const r = raw?.data ?? raw;

  return {
    // ---- required by MosqueInformationDonationPage ----
    requestId: Number(r?.requestId ?? r?.request_id ?? r?.DDR_Id ?? r?.ddr_id ?? r?.id ?? 0) || 0,
    requestName: (r?.requestName ?? r?.request_name ?? r?.request_number ?? r?.requestNameAr ?? null) as string | null,

    mosqueId: Number(r?.mosqueId ?? r?.mosque_id ?? 0) || 0,
    mosqueName: (r?.mosqueName ?? r?.mosque_name ?? null) as string | null,
    number: (r?.number ?? r?.mosque_number ?? r?.serial_number ?? r?.mosqueNumber ?? null) as string | null,

    latitude: r?.latitude != null ? Number(r.latitude) : null,
    longitude: r?.longitude != null ? Number(r.longitude) : null,

    classificationId: r?.classificationId != null ? Number(r.classificationId) : r?.classification_id != null ? Number(r.classification_id) : null,
    classificationName: (r?.classificationName ?? r?.classification_name ?? null) as string | null,

    regionName: (r?.regionName ?? r?.region_name ?? null) as string | null,
    cityName: (r?.cityName ?? r?.city_name ?? null) as string | null,
    moiaCenterName: (r?.moiaCenterName ?? r?.moia_center_name ?? null) as string | null,
    fullAddress: (r?.fullAddress ?? r?.full_address ?? null) as string | null,
    districtName: (r?.districtName ?? r?.district_name ?? null) as string | null,

    catAId: r?.catAId != null ? Number(r.catAId) : r?.cat_a_id != null ? Number(r.cat_a_id) : null,
    catAName: (r?.catAName ?? r?.cat_a_name ?? null) as string | null,

    catBId: r?.catBId != null ? Number(r.catBId) : r?.cat_b_id != null ? Number(r.cat_b_id) : null,
    catBName: (r?.catBName ?? r?.cat_b_name ?? null) as string | null,

    catCId: r?.catCId != null ? Number(r.catCId) : r?.cat_c_id != null ? Number(r.cat_c_id) : null,
    catCName: (r?.catCName ?? r?.cat_c_name ?? r?.cat_c_names ?? null) as string | null,

    catDId: r?.catDId != null ? Number(r.catDId) : r?.cat_d_id != null ? Number(r.cat_d_id) : null,
    catDName: (r?.catDName ?? r?.cat_d_name ?? null) as string | null,

    catEIds: r?.catEIds ?? r?.cat_e_ids ?? null,
    catENames: (r?.catENames ?? r?.cat_e_names ?? null) as string | null,

    // ---- keep the rest (not required by your current UI, but safe) ----
    mosqueOpeningDate: r?.mosqueOpeningDate ?? r?.mosque_opening_date ?? null,
    urbanCondition: r?.urbanCondition ?? r?.urban_condition ?? null,
    dateMaintenanceLast: r?.dateMaintenanceLast ?? r?.date_maintenance_last ?? null,
    fridayPrayerRows: r?.fridayPrayerRows ?? r?.friday_prayer_rows ?? null,
    surface: r?.surface ?? null,
    landArea: r?.landArea ?? r?.land_area ?? null,

    lengthRowMenPraying: r?.lengthRowMenPraying ?? r?.length_row_men_praying ?? null,
    rowMenPrayingNumber: r?.rowMenPrayingNumber ?? r?.row_men_praying_number ?? null,
    toiletMenNumber: r?.toiletMenNumber ?? r?.toilet_men_number ?? null,
    menPrayerAvgAttendance: r?.menPrayerAvgAttendance ?? r?.men_prayer_avg_attendance ?? null,

    lengthRowWomenPraying: r?.lengthRowWomenPraying ?? r?.length_row_women_praying ?? null,
    rowWomenPrayingNumber: r?.rowWomenPrayingNumber ?? r?.row_women_praying_number ?? null,
    womenPrayerRoomCapacity: r?.womenPrayerRoomCapacity ?? r?.women_prayer_room_capacity ?? null,
    isWomenToilets: r?.isWomenToilets ?? r?.is_women_toilets ?? r?.has_women_prayer_room ?? null,
    toiletWomanNumber: r?.toiletWomanNumber ?? r?.toilet_woman_number ?? null,

    internalDoorsNumber: r?.internalDoorsNumber ?? r?.internal_doors_number ?? null,
    internalHeadsetNumber: r?.internalHeadsetNumber ?? r?.internal_headset_number ?? null,
    internalSpeakerNumber: r?.internalSpeakerNumber ?? r?.internal_speaker_number ?? null,

    hasExternalCamera: r?.hasExternalCamera ?? r?.has_external_camera ?? null,
    hasInternalCamera: r?.hasInternalCamera ?? r?.has_internal_camera ?? null,
    externalDoorsNumbers: r?.externalDoorsNumbers ?? r?.external_doors_numbers ?? null,
    externalHeadsetNumber: r?.externalHeadsetNumber ?? r?.external_headset_number ?? null,
    externalSpeakerNumber: r?.externalSpeakerNumber ?? r?.external_speaker_number ?? null,

    numMinarets: r?.numMinarets ?? r?.num_minarets ?? null,
    numFloors: r?.numFloors ?? r?.num_floors ?? null,
    hasBasement: r?.hasBasement ?? r?.has_basement ?? null,
    mosqueRooms: r?.mosqueRooms ?? r?.mosque_rooms ?? null,
    nonBuildingArea: r?.nonBuildingArea ?? r?.non_building_area ?? null,
    roofedArea: r?.roofedArea ?? r?.roofed_area ?? null,
    isFreeArea: r?.isFreeArea ?? r?.is_free_area ?? null,

    hasAirConditioners: r?.hasAirConditioners ?? r?.has_air_conditioners ?? null,
    acType: r?.acType ?? r?.ac_type ?? null,
    numAirConditioners: r?.numAirConditioners ?? r?.num_air_conditioners ?? null,
    numLightingInside: r?.numLightingInside ?? r?.num_lighting_inside ?? null,

    hasFireExtinguishers: r?.hasFireExtinguishers ?? r?.has_fire_extinguishers ?? null,
    hasFireSystemPumps: r?.hasFireSystemPumps ?? r?.has_fire_system_pumps ?? null,

    mosqueHistorical: r?.mosqueHistorical ?? r?.mosque_historical ?? null,
  };
}

/**
 * ✅ This is what your page expects to import/call.
 * It uses the existing HDR endpoint and returns a normalized object.
 */
export async function getMosqueHygieneDonationOpportunityByRequestId(id: string | number) {
  const raw = await getHygieneDdrById(id);
  return mapHygieneDetailToMosqueOpportunityDetail(raw);
}